import {
  ConditionType,
  ExpenseOperator,
  AmountOperator,
  CounterpartyOperator,
  ApprovalMode,
  ApprovalCondition
} from 'openapi';

import {
  chipAutoApproveStyle,
  chipParallelStyle,
  chipSequentialStyle
} from 'styles/pages/ApprovalsStyle';

export const getApprovalStatus = (
  isApproved: boolean,
  approvedBy: string | null | undefined,
  approvers: string[] | undefined,
  translate: (key: string) => string
): { approvalText: string; approvalValue: string } => {
  if (isApproved) {
    if (approvedBy) {
      // Case: Approved By
      const approvalText = `${translate('labels.approvedBy')}: ${approvedBy}`;
      const approvalValue = approvedBy;
      return { approvalText, approvalValue };
    }
    // Case: Auto Approved
    const autoApproved = translate('labels.autoApproved');
    return { approvalText: autoApproved, approvalValue: autoApproved };
  }
  // Case: Awaiting Approval From
  const approversList = approvers?.join(', ') || '-';
  const approvalText = `${translate(
    'labels.awaitingApprovalFrom'
  )}: ${approversList}`;
  return { approvalText, approvalValue: approversList };
};

// ApprovalRule-specific helpers
export const getApprovalModeLabel = (
  mode?: ApprovalMode,
  translate?: (key: string) => string
): string => {
  const t = translate || ((key: string) => key);
  switch (mode) {
    case ApprovalMode.AUTO_APPROVE:
      return t('approvals.autoApprove');
    case ApprovalMode.SEQUENTIAL:
      return t('approvals.sequential');
    case ApprovalMode.PARALLEL:
      return t('approvals.parallel');
    default:
      return '';
  }
};

export const getApprovalModeChipStyle = (mode?: string) => {
  switch (mode) {
    case ApprovalMode.AUTO_APPROVE:
      return chipAutoApproveStyle;
    case ApprovalMode.SEQUENTIAL:
      return chipSequentialStyle;
    case ApprovalMode.PARALLEL:
      return chipParallelStyle;
    default:
      return {};
  }
};

export const getApproversText = (
  steps?: Array<{
    approver?: {
      name?: string;
    };
  }>,
  approvalMode?: ApprovalMode,
  translate?: (key: string) => string
): string => {
  if (!steps || steps.length === 0) return '';
  const t = translate || ((key: string) => key);
  const separator = approvalMode === ApprovalMode.PARALLEL ? ', ' : ' → ';
  return steps
    .map((step) => step.approver?.name || t('approvals.unknown'))
    .join(separator);
};

export const getApprovalRuleConditionText = (
  condition: ApprovalCondition,
  translate?: (module: string, params?: Record<string, any>) => string
): string => {
  const t = translate || ((key: string) => key);
  switch (condition.conditionType) {
    case ConditionType.EXPENSE: {
      const op = condition.operator;
      const operatorMap: Partial<Record<ExpenseOperator, string>> = {
        [ExpenseOperator.IS]: t('approvals.is'),
        [ExpenseOperator.IS_NOT]: t('approvals.isNot'),
        [ExpenseOperator.IS_NONE_OF]: t('approvals.isNoneOf'),
        [ExpenseOperator.IS_ANY_OF]: t('approvals.isAnyOf')
      };
      return `${t('approvals.expenseType')} ${operatorMap[op] || t('approvals.is')} (${condition.expenseTypes?.map((et) => et.name).join(', ') || ''})`;
    }
    case ConditionType.AMOUNT: {
      const op = condition.operator;
      const operatorMap: Partial<Record<AmountOperator, string>> = {
        [AmountOperator.EQUALS]: t('approvals.equals'),
        [AmountOperator.BETWEEN]: t('approvals.isBetween'),
        [AmountOperator.GREATER_THAN]: t('approvals.isGreaterThan'),
        [AmountOperator.GREATER_THAN_OR_EQUAL]: t(
          'approvals.isGreaterThanOrEqualTo'
        ),
        [AmountOperator.LESS_THAN]: t('approvals.isLessThan'),
        [AmountOperator.LESS_THAN_OR_EQUAL]: t('approvals.isLessThanOrEqualTo')
      };
      const operatorText = operatorMap[op] || t('approvals.equals');
      if (op === AmountOperator.BETWEEN) {
        return `${t('approvals.amount')} ${operatorText} ${condition.amountFrom} - ${condition.amountTo} ${condition.currency?.isoCode || ''}`;
      }
      return `${t('approvals.amount')} ${operatorText} ${condition.amountFrom} ${condition.currency?.isoCode || ''}`;
    }
    case ConditionType.COUNTERPARTY: {
      const op = condition.operator;
      const operatorMap: Partial<Record<CounterpartyOperator, string>> = {
        [CounterpartyOperator.IS]: t('approvals.is'),
        [CounterpartyOperator.IS_ANY_OF]: t('approvals.isAnyOf'),
        [CounterpartyOperator.IS_VALIDATED]: t('approvals.isValidated'),
        [CounterpartyOperator.IS_NOT_VALIDATED]: t('approvals.isNotValidated')
      };
      // For IS_VALIDATED and IS_NOT_VALIDATED, don't show counterparty names
      if (
        op === CounterpartyOperator.IS_VALIDATED ||
        op === CounterpartyOperator.IS_NOT_VALIDATED
      ) {
        return `${t('approvals.counterparty')} ${operatorMap[op] || t('approvals.is')}`;
      }
      const counterpartyNames =
        condition.counterparties?.map((cp) => cp.name).join(', ') || '';
      const isMulti = op === CounterpartyOperator.IS_ANY_OF;
      const formattedNames = isMulti
        ? `(${counterpartyNames})`
        : counterpartyNames;
      return `${t('approvals.counterparty')} ${operatorMap[op] || t('approvals.is')} ${formattedNames}`;
    }
    default:
      return '';
  }
};
